import { Page, arrayUtils } from "@zickko/shared";
import { ReactNode, forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";
import { Cursor } from "../services/firestoreApi";
import { asyncOperation } from "../utils/asyncOperation";

export type RenderItemProps<T> = { item: T; onDelete: (item: T) => void };

type Props<T> = {
  renderHeader: () => ReactNode;
  renderItem: (args: RenderItemProps<T>) => ReactNode;
  renderCreateButton?: () => ReactNode;
  getPage: (cursor?: Cursor) => Promise<Page<T>>;
  emptyStateHeading: string;
  emptyStateSubHeading: string;
};

export type CollectionListWidgetRef = {
  refresh: () => void;
};

// K = Key, T = Type
export const CollectionListWidget = forwardRef(<T,>(props: Props<T>, ref) => {
  //
  const [list, setList] = useState<T[] | undefined>();
  const [cursor, setCursor] = useState<Cursor | undefined>();
  const [loading, setLoading] = useState(false);

  const refresh = () => {
    setList(undefined);
    setCursor(undefined);
    fetchPage();
  };

  useImperativeHandle<any, CollectionListWidgetRef>(
    ref,
    () => {
      return {
        refresh,
      };
    },
    []
  );

  const fetchPage = useCallback(
    (cursor?: Cursor) => {
      asyncOperation({
        fn: () => props.getPage(cursor),
        setBusyFlag: setLoading,
        loadingMessage: "Loading ...",
        onSuccess: (page) => {
          const newList = [...(list ?? []), ...page.list];
          setList(newList);
          setCursor(page.cursor);
          // props.onListUpdate?.(newList);
        },
      });
    },
    [props.getPage]
  );

  useEffect(() => {
    fetchPage(cursor);
  }, []);

  if (list === undefined) {
    return <div>Loading ...</div>;
  }

  if (list.length === 0) {
    return (
      <div className="empty-user">
        <div className="heading">{props.emptyStateHeading}</div>
        <div className="sub-heading">{props.emptyStateSubHeading}</div>
        {props.renderCreateButton?.()}
      </div>
    );
  }

  const renderLoadMoreButton = () => {
    return <button disabled={loading}>{loading ? "Loading ..." : "Load More"}</button>;
  };

  // remove this item from the list
  const onDelete = (index: number) => {
    return () => setList(arrayUtils.remove(list, index));
  };

  return (
    <div className={`list-widget`}>
      {props.renderHeader()}
      {list.map((item, index) => props.renderItem({ item, onDelete: onDelete(index) }))}
      {/*renderLoadMoreButton()*/}
    </div>
  );
});
